import apiClient from "../api/axiosConfig";
import { ENDPOINTS } from "../api/endpoints";
import {
  ApiResponse,
  CompanyClientResponse,
  MonthResponses,
  OrderCartPayload,
  OrderDetailResponse,
  OrderDetailsResult,
  OrderJsonResponse,
  OrdersListPayload,
  ProductType,
  SaveOrderProductsPayload,
} from "../../types/api.types";

function normalizeOrderFormLineFromSave(row: any): OrderJsonResponse {
  const months: MonthResponses[] = Array.isArray(row?.months)
    ? row.months.map((m: any) => {
        const monthName = String(m.month ?? "").trim();
        const yearStr = m.year != null ? String(m.year).trim() : "";
        const label =
          monthName && yearStr
            ? `${monthName} ${yearStr}`.trim()
            : monthName || yearStr;
        return {
          month: label,
          observation: String(m.observation ?? ""),
          quantity: Number(m.quantity) || 0,
          inStock: Boolean(m.inStock),
          monthNumber: Number(m.monthNumber) || 0,
        };
      })
    : [];
  return {
    productId: String(row?.productId ?? ""),
    orderDetailId:
      row?.orderDetailId != null ? String(row.orderDetailId).trim() : undefined,
    code: String(row?.code ?? "").trim(),
    description: String(row?.description ?? ""),
    observation: String(row?.observation ?? ""),
    type: String(row?.type ?? ""),
    price: Number(row?.price) || 0,
    months,
  };
}

export const orderService = {
  getSettings: async (): Promise<{ minDiscountPercentage: number; maxDiscountPercentage: number }> => {
    const response = await apiClient.get(ENDPOINTS.ORDERS.SETTINGS);
    return response.data.data;
  },

  checkStock: async (orderId: string, detail: OrderCartPayload["details"][number]): Promise<{ hasStock: boolean; errors: string[] }> => {
    const response = await apiClient.post(ENDPOINTS.ORDERS.CHECK_STOCK, detail, { params: { orderId } });
    return response.data.data;
  },
  getOrders: async (
    currentPage: number,
    pageSize: number,
    clientId: string,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<OrdersListPayload> => {
    const params: Record<string, string | number> = {
      page: currentPage,
      rowPerPage: pageSize,
      clientId,
    };
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    const response = await apiClient.get<ApiResponse<OrdersListPayload>>(
      ENDPOINTS.ORDERS.GET_ALL,
      { params },
    );
    const data =
      response.data?.data ?? (response.data as unknown as OrdersListPayload);
    return {
      orders: data?.orders ?? [],
      total: data?.total ?? 0,
    };
  },

  getOrderForm: async (orderId?: string): Promise<OrderJsonResponse[]> => {
    const company = JSON.parse(
      localStorage.getItem("company") || "{}",
    ) as CompanyClientResponse;
    const params: {
      clientId: string;
      orderId?: string;
    } = {
      clientId: company.clientID,
    };
    if (orderId != null && orderId !== "") {
      params.orderId = orderId;
    }
    const response = await apiClient.get<ApiResponse<OrderJsonResponse[]>>(
      ENDPOINTS.ORDERS.GET_FORM,
      { params },
    );
    const raw = response.data?.data ?? response.data;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((row: any) => normalizeOrderFormLineFromSave(row));
  },

  getOrderDetails: async (orderId: string): Promise<OrderDetailsResult> => {
    const response = await apiClient.get<
      ApiResponse<OrderDetailResponse[]> & {
        status?: string;
        observations?: string;
      }
    >(ENDPOINTS.ORDERS.GET_DETAILS, { params: { orderId } });
    const body = response.data as ApiResponse<OrderDetailResponse[]> & {
      status?: string;
      observations?: string;
      consecutiveNumber?: number;
      discount?: number;
    };
    const data = body?.data;
    const items = Array.isArray(data) ? data : [];
    const formLines =
      items.length > 0 &&
      Array.isArray((items[0] as { months?: unknown })?.months)
        ? items.map((row: any) => normalizeOrderFormLineFromSave(row))
        : undefined;
    //@ts-ignore
    const obs = body?.obsertavions;
    return {
      items,
      formLines,
      status: body?.status,
      observations: obs !== undefined && obs !== null ? String(obs) : undefined,
      consecutiveNumber: body?.consecutiveNumber ?? null,
      discount: body?.discount ?? 0,
    };
  },

  getProductTypes: async (): Promise<ProductType[]> => {
    const response = await apiClient.get<ApiResponse<ProductType[]>>(
      ENDPOINTS.ORDERS.GET_PRODUCT_TYPES,
    );
    const raw = response.data.data ?? response.data;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((item: any) => ({
      productTypeID:
        item.productTypeID ?? item.productTypeId ?? item.id ?? item,
      name:
        item.name ??
        item.description ??
        String(item.productTypeID ?? item.productTypeId ?? item.id ?? item),
      description: item.description,
    }));
  },

  /** POST /api/orders/saveProducts — devuelve el mismo cuerpo que el formulario de pedido (líneas + meses). */
  saveOrderProducts: async (
    payload: SaveOrderProductsPayload,
  ): Promise<OrderJsonResponse[]> => {
    const response = await apiClient.post<ApiResponse<OrderJsonResponse[]>>(
      ENDPOINTS.ORDERS.SAVE_PRODUCTS,
      payload,
    );
    const raw = response.data?.data ?? response.data;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((row: any) => normalizeOrderFormLineFromSave(row));
  },

  /** POST /api/orders?orderId=… */
  postOrder: async (
    orderId: string,
    payload: OrderCartPayload,
  ): Promise<void> => {
    await apiClient.post(ENDPOINTS.ORDERS.POST_ORDER, payload, {
      params: { orderId },
    });
  },

  /** POST /api/orders/sent?orderId=… body: `{ observations }` */
  sendOrder: async (orderId: string, observations?: string): Promise<void> => {
    await apiClient.post(
      ENDPOINTS.ORDERS.SENT,
      { observations: observations?.trim() ?? "" },
      {
        params: { orderId },
      },
    );
  },

  /** DELETE /api/orders/deleteDetail?orderId=…&orderDetailId=… */
  deleteOrderDetail: async (
    orderId: string,
    orderDetailId: string,
  ): Promise<void> => {
    await apiClient.delete(ENDPOINTS.ORDERS.DELETE_DETAIL, {
      params: { orderId, orderDetailId },
    });
  },

  exportOrder: async (orderId: string): Promise<Blob> => {
    const response = await apiClient.post<Blob>(
      ENDPOINTS.ORDERS.EXPORT(orderId),
      {},
      { responseType: "blob" },
    );
    return response.data;
  },
};
